import React, {useEffect, useState} from 'react';
import classes from "./CreatePayment.module.scss";
import Container from "../../components/UI/Container/Container";
import Block from "../../components/UI/Block/Block";
import {
  Avatar,
  Button,
  Col, Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
  Tag,
  Typography, Upload
} from "antd";
import {useNavigate, useSearchParams} from "react-router-dom";
import {PAYMENT_SYSTEM, TRANSACTION_TYPE} from "../../utils/constants";
import {ROUTES} from "../../components/AppRouter/AppRouter";
import {FIELDS_SCHEME, GENERAL_SCHEME} from "../../schemas";
import {API} from "../../http";
import {NEW_ENDPOINTS} from "../../http/endpoints";
import {FileTextOutlined, HomeOutlined, UserOutlined} from "@ant-design/icons";
import moment from "moment/moment";
import {fetchTariffs, fetchUsers} from "../../actions/users";
import {countries} from "../../utils/countries";
import {handleFileToBase64} from "../../utils/helpers";

const CreatePayment = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [tariffs, setTariffs] = useState([]);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedUserId = Form.useWatch('user_id', form);
  const selectedUser = users.find(user => user.id === selectedUserId);
  const selectedUserTariff = tariffs.find(tariff => tariff.id === selectedUser?.tariff);

  // CHECKERS
  const payment = searchParams.get('payment');
  const type = searchParams.get('type');
  const isValidPayment = Object.values(PAYMENT_SYSTEM).includes(payment);
  const isValidType = Object.values(TRANSACTION_TYPE).includes(type);
  useEffect(() => {
    if (!isValidPayment || !isValidType) {
      navigate(ROUTES.DASHBOARD.path);
    }
  }, [payment, type, navigate, isValidPayment, isValidType]);

  // INITIAL
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const users = await fetchUsers();
        setUsers(users);
        const tariffs = await fetchTariffs();
        setTariffs(tariffs);
        setLoading(false);
      } catch (e) {
        setLoading(false);
      }
    }

    fetchInitialData();
  }, []);

  // MAIN
  const handleSubmit = async ({invoice, contract, ...formData}) => {
    const user = users.find(u => u.id === formData.user_id);

    if (!user) return;

    if (user.is_corporate && type === TRANSACTION_TYPE.SEND) {
      const invoiceFile = invoice?.fileList?.[0]?.originFileObj;
      const contractFile = contract?.fileList?.[0]?.originFileObj;

      if (!invoiceFile && !contractFile) {
        message.error('Please upload at least one document (invoice or contract)');
        return;
      }
    }

    const fullName = `${user.first_name} ${user.last_name}`;
    const commonFields = {
      sumsub_id: user.sumsub_id,
      user_id: user.id,
    }, transactionFields = type === TRANSACTION_TYPE.SEND ? {
      from_name: fullName,
      from_account: user.account_id,
      from_address: "N/A",
      from_address_country: user.country ?? "N/A",
      from_address_city: user.city ?? "N/A",
      from_address_zip: user.zip_code ?? "N/A",
      from_bic: "N/A",
      from_reference: "N/A",
    } : {
      to_name: fullName,
      to_account: user.account_id,
      to_address: "N/A",
      to_address_country: user.country ?? "N/A",
      to_address_city: user.city ?? "N/A",
      to_address_zip: user.zip_code ?? "N/A",
      to_bic: "N/A",
      to_reference: "N/A",
    };

    let documents = [];
    const invoiceFile = invoice?.fileList?.[0]?.originFileObj;
    const contractFile = contract?.fileList?.[0]?.originFileObj;

    if (invoiceFile) {
      const invoiceBase64 = await handleFileToBase64(invoiceFile);
      documents.push({
        name: 'Invoice',
        base64string: invoiceBase64
      });
    }

    if (contractFile) {
      const contractBase64 = await handleFileToBase64(contractFile);
      documents.push({
        name: 'Contract',
        base64string: contractBase64
      });
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        ...commonFields,
        ...transactionFields,
        payment_system: payment,
        type,
        currency: formData.amount.currency,
        amount: formData.amount.amount,
      };

      if (documents.length > 0) payload.documents = documents;

      await API.post(NEW_ENDPOINTS.ADMIN.ORDER.CREATE, payload);
      message.success('Transaction was created successfully');
      navigate(ROUTES.DASHBOARD.path);
    } catch (e) {
      message.error(e?.data?.description?.name || e?.data?.description || 'Something went wrong...');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (file) => {
    if (file.type !== 'application/pdf') {
      message.error('Only PDF files are allowed');
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > 100) {
      message.error('File must be smaller than 100MB');
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return (
    <Container>
      <Block style={{ padding: 30 }}>
        <Typography.Title style={{ marginBottom: 40 }} level={2}>Create {payment} {type === TRANSACTION_TYPE.SEND ? 'outgoing' : 'incoming'} payment</Typography.Title>

        <Form
          form={form}
          disabled={loading}
          onFinish={handleSubmit}
          className={classes.form}
          initialValues={{payment_system: payment, type}}
          size="large"
          variant="filled"
          labelCol={{xs: 24}}
          labelAlign="left"
        >
          <Row justify="space-between" style={{ marginBottom: 20 }} gutter={40}>
            <Col xs={24} lg={12}>
              <Space direction="vertical" className="w100">
                <Form.Item
                  name="user_id"
                  label={`Select ${type === TRANSACTION_TYPE.SEND ? 'Sender' : 'Beneficiary'}`}
                  rules={GENERAL_SCHEME.required}
                >
                  <Select
                    optionFilterProp="children"
                    placeholder="Select a user"
                    filterOption={(input, option) => {
                      return option.children.props.children[1].props.children.join().toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }}
                    loading={loading}
                    showSearch
                  >
                    {users.map(user => (
                      <Select.Option key={user.id} value={user.id}>
                        <Space>
                          {user.is_corporate ? (
                            <Tag style={{width: 30, textAlign: 'center'}} bordered={false} color="volcano">C</Tag>
                          ) : (
                            <Tag style={{width: 30, textAlign: 'center'}} bordered={false} color="blue">I</Tag>
                          )}
                          <Typography.Text>
                            {user.first_name} {user.last_name} ({user.email_address})
                          </Typography.Text>
                        </Space>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Row justify="space-between" gutter={20}>
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Amount"
                      required
                    >
                      <Input.Group compact>
                        <Form.Item
                          name={['amount', 'amount']}
                          dependencies={['amount', 'currency']}
                          noStyle
                          rules={[
                            ...GENERAL_SCHEME.required,
                            ({ getFieldValue, getFieldsValue }) => ({
                              validator(_, value) {
                                if (!value || type === TRANSACTION_TYPE.RECEIVE) {
                                  return Promise.resolve();
                                }

                                const currency = getFieldValue(['amount', 'currency']);
                                const maxAmount = selectedUserTariff
                                  ? (payment === 'sepa'
                                    ? selectedUserTariff.sepa
                                    : selectedUserTariff.swift)
                                  : Infinity;

                                const userBalance = currency === 'EUR'
                                  ? selectedUser?.balance ?? Infinity
                                  : selectedUser?.balance_gbp ?? Infinity;

                                if (value <= maxAmount && value <= userBalance) {
                                  return Promise.resolve();
                                }

                                if (value > userBalance) {
                                  return Promise.reject(new Error(`Amount exceeds user's balance of ${userBalance.toFixed(2)} ${currency}`));
                                }

                                return Promise.reject(new Error(`Amount exceeds maximum allowed value of ${maxAmount.toFixed(2)} ${currency} for ${payment}`));
                              }
                            })
                          ]}
                        >
                          <InputNumber
                            placeholder="Amount"
                            disabled={!selectedUser}
                            min={0}
                            step={0.01}
                            precision={2}
                            style={{ width: '60%' }}
                          />
                        </Form.Item>
                        <Form.Item
                          initialValue="EUR"
                          name={['amount', 'currency']}
                          noStyle
                          rules={[{ required: true, message: 'Please select a currency!' }]}
                        >
                          <Select disabled={!selectedUser} style={{ width: '40%' }}>
                            <Select.Option disabled={selectedUser?.balance === undefined} value="EUR">EUR</Select.Option>
                            <Select.Option disabled={selectedUser?.balance_gbp === undefined} value="GBP">GBP</Select.Option>
                          </Select>
                        </Form.Item>
                      </Input.Group>
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={6}>
                    <Form.Item rules={GENERAL_SCHEME.required} label="Our Fee" name="fee" required>
                      <InputNumber placeholder="Our Fee" disabled={!selectedUser} suffix="EUR" min={0} step={1} precision={1} style={{width: '100%'}}/>
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Form.Item rules={GENERAL_SCHEME.required} label="Correspondence Bank Fee" name="fee_tesla" required>
                      <InputNumber placeholder="Correspondence Bank Fee" disabled={!selectedUser} suffix="EUR" min={0} step={1} precision={1} style={{width: '100%'}}/>
                    </Form.Item>
                  </Col>
                </Row>
              </Space>

              {selectedUser && (
                <React.Fragment>
                  <Divider/>

                  <div className={classes.selectedUserContainer}>
                    <Avatar size={80} icon={selectedUser?.is_corporate ? <HomeOutlined/> : <UserOutlined/>}/>
                    <Typography.Title level={4}>{selectedUser.first_name} {selectedUser.last_name}</Typography.Title>
                    <Descriptions column={{xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2}}>
                      <Descriptions.Item label="Balance">
                        {selectedUser.balance?.toFixed(2) ?? '-/-'} EUR | {selectedUser.balance_gbp?.toFixed(2) ?? '-/-'} GBP
                      </Descriptions.Item>
                      <Descriptions.Item label="sumsub_id">
                        <Typography.Text copyable={selectedUser.sumsub_id}>
                          {selectedUser.sumsub_id ?? '-/-'}
                        </Typography.Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="risk_level">
                        {selectedUser.risk_level}
                      </Descriptions.Item>
                      <Descriptions.Item label="Address">
                        {[selectedUser.country, selectedUser.city, selectedUser.zip_code].filter(Boolean).join(', ') || '-/-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Mobile Number">
                        {selectedUser.mobile_number ?? '-/-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Tariff">
                        {(() => {
                          const selectedTariff = tariffs.find(t => t.id === selectedUser.tariff);
                          if (!selectedTariff) {
                            return '-/-';
                          } else if (selectedTariff.name === null) {
                            return selectedTariff.id;
                          } else {
                            return selectedTariff.name;
                          }
                        })()}
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                </React.Fragment>
              )}
            </Col>
            <Col xs={24} lg={12}>
              <div className={classes.paymentFormContainer}>
                <Typography.Title
                  level={4}>Provide information about the {type === TRANSACTION_TYPE.SEND ? 'BENEFICIARY' : 'SENDER'} of the payment:</Typography.Title>
                <Form.Item
                  name={type === TRANSACTION_TYPE.SEND ? 'to_name' : 'from_name'}
                  label="Name"
                  rules={[...GENERAL_SCHEME.required, ...GENERAL_SCHEME.alphaSpace]}
                  required
                >
                  <Input placeholder="Name"/>
                </Form.Item>
                <Form.Item
                  name={type === TRANSACTION_TYPE.SEND ? 'to_account' : 'from_account'}
                  label="Account"
                  rules={[...GENERAL_SCHEME.required, ...GENERAL_SCHEME.alphaNumeric]}
                  required
                >
                  <Input placeholder="Account"/>
                </Form.Item>
                <Row justify="space-between" gutter={20}>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name={type === TRANSACTION_TYPE.SEND ? 'to_address_country' : 'from_address_country'}
                      label="Country"
                      rules={GENERAL_SCHEME.required}
                      required
                    >
                      <Select options={Object.values(countries).map(country => ({
                        label: country.cn,
                        value: country.cc3,
                      }))} placeholder="Country" optionFilterProp="children"
                              filterOption={(input, option) => {
                                return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                              }} showSearch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name={type === TRANSACTION_TYPE.SEND ? 'to_address_city' : 'from_address_city'}
                      label="City"
                      rules={[...GENERAL_SCHEME.required, ...FIELDS_SCHEME.address]}
                      required
                    >
                      <Input placeholder="City"/>
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item
                      name={type === TRANSACTION_TYPE.SEND ? 'to_address' : 'from_address'}
                      label="Address"
                      rules={[...GENERAL_SCHEME.required, ...FIELDS_SCHEME.address]}
                      required
                    >
                      <Input placeholder="Address" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name={type === TRANSACTION_TYPE.SEND ? 'to_address_num' : 'from_address_num'}
                      label="House Number"
                      rules={[...GENERAL_SCHEME.required, ...GENERAL_SCHEME.numeric]}
                      required
                    >
                      <InputNumber placeholder="House Number" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Form.Item
                      name={type === TRANSACTION_TYPE.SEND ? 'to_address_zip' : 'from_address_zip'}
                      label="Postal Code"
                      rules={GENERAL_SCHEME.required}
                      required
                    >
                      <Input placeholder="Postal Code"/>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item
                  name={type === TRANSACTION_TYPE.SEND ? 'to_bic' : 'from_bic'}
                  label="BIC Code"
                  rules={[...GENERAL_SCHEME.required, ...GENERAL_SCHEME.alphaNumeric]}
                  required
                >
                  <Input placeholder="BIC"/>
                </Form.Item>
                <Form.Item
                  name={type === TRANSACTION_TYPE.SEND ? 'to_reference' : 'from_reference'}
                  label="Reference"
                  rules={GENERAL_SCHEME.required}
                  required
                >
                  <Input placeholder="Payment Reference"/>
                </Form.Item>
                <Form.Item
                  name="description"
                  label={`Payment Description${selectedUser?.is_corporate ? '' : ' (optional)'}`}
                  rules={selectedUser?.is_corporate && GENERAL_SCHEME.required}
                  required={selectedUser?.is_corporate ?? false}
                >
                  <Input.TextArea placeholder="Payment Description" autoSize={{ minRows: 2, maxRows: 6 }} />
                </Form.Item>

                <Form.Item label={`Invoice Document${(selectedUser?.is_corporate && type === TRANSACTION_TYPE.RECEIVE) || !selectedUser?.is_corporate ? ' (optional)' : ''}`} name="invoice">
                  <Upload.Dragger accept="application/pdf" listType="picture" maxCount={1} multiple={false} beforeUpload={handleFileUpload}>
                    <p className="ant-upload-drag-icon">
                      <FileTextOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag the invoice document to this area</p>
                    <p className="ant-upload-hint">
                      Only one PDF file is allowed. The file must be smaller than 100MB.
                    </p>
                  </Upload.Dragger>
                </Form.Item>
                <Form.Item label={`Contract Document${(selectedUser?.is_corporate && type === TRANSACTION_TYPE.RECEIVE) || !selectedUser?.is_corporate ? ' (optional)' : ''}`} name="contract">
                  <Upload.Dragger accept="application/pdf" listType="picture" maxCount={1} multiple={false} beforeUpload={handleFileUpload}>
                    <p className="ant-upload-drag-icon">
                      <FileTextOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag the contract document to this area</p>
                    <p className="ant-upload-hint">
                      Only one PDF file is allowed. The file must be smaller than 100MB.
                    </p>
                  </Upload.Dragger>
                </Form.Item>
              </div>
            </Col>
          </Row>

          <div style={{display: 'flex', justifyContent: 'center'}}>
            <Button style={{maxWidth: 300}} size="large" shape="round" type="primary" htmlType="submit" block>
              Submit Transaction
            </Button>
          </div>
        </Form>
      </Block>
    </Container>
  );
};

export default CreatePayment;
